import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { OffersAction } from 'store/offers/OffersAction'
import { toast } from 'utils/toastUtils'
import type { Offer } from 'types/entities/Offer'
import type { CompanyAddress } from 'types/entities/CompanyAddress'

const isAddressValidForBooking = (address?: CompanyAddress | null) => {
    if (!address || address.deleted_at) {
        return false
    }

    return Boolean(address.id)
}

const resolveAddressIdFromCompany = (addresses: CompanyAddress[], defaultAddressId?: number | null) => {
    const validAddresses = addresses.filter((address) => isAddressValidForBooking(address))

    if (!validAddresses.length) {
        return null
    }

    if (defaultAddressId) {
        const defaultAddress = validAddresses.find((address) => address.id === defaultAddressId)
        if (defaultAddress) {
            return defaultAddress.id
        }
    }

    return validAddresses[0]?.id ?? null
}

export const useEnsureOfferAddressForBooking = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    return useCallback(async (offer: Offer): Promise<boolean> => {
        if (!offer.company_id) {
            toast.error(t('Company is required to create booking'))
            return false
        }

        let resolvedAddressId: number | null = isAddressValidForBooking(offer.address)
            ? offer.address?.id ?? null
            : null

        if (!resolvedAddressId) {
            resolvedAddressId = resolveAddressIdFromCompany(
                offer.company?.addresses || [],
                offer.company?.default_address_id
            )
        }

        if (!resolvedAddressId && offer.address_id) {
            try {
                const selectedAddress = await dispatch(CompaniesAction.fetchCompanyAddressById(offer.address_id))
                if (isAddressValidForBooking(selectedAddress)) {
                    resolvedAddressId = selectedAddress.id
                }
            } catch (e) {
                console.error(e)
            }
        }

        if (!resolvedAddressId) {
            try {
                const company = await dispatch(CompaniesAction.fetchCompanyById(offer.company_id))
                resolvedAddressId = resolveAddressIdFromCompany(
                    company?.addresses || [],
                    company?.default_address_id
                )
            } catch (e) {
                console.error(e)
            }
        }

        if (!resolvedAddressId) {
            toast.error(t('Company address is required before creating booking'))
            return false
        }

        if (offer.address_id !== resolvedAddressId) {
            await dispatch(
                OffersAction.updateOffer({
                    id: offer.id,
                    seller_id: offer.seller_id,
                    company_id: offer.company_id,
                    address_id: resolvedAddressId,
                })
            )
        }

        return true
    }, [dispatch, t])
}
